/*
 * Restyles the Shopify Forms app block rendered inside `.sm-quoteform .qf-card`.
 * The app renders its fields inside an open shadow root (<shopify-forms-embed>),
 * so a normal theme stylesheet can't reach it — this injects a scoped <style>
 * into that shadow root and adds group-title elements to match
 * input/SafeMoving-Atlanta.html's "Contact Information / Pickup Location /
 * Safe Information / Destination Location / Additional Information" layout.
 *
 * No existing app-rendered nodes are moved or removed (only new <p> group
 * titles are inserted as extra siblings) so the app's own re-renders can't
 * be broken by this script.
 */
(function () {
  'use strict';

  var SANS = "var(--sans, -apple-system,'Segoe UI',Roboto,Helvetica,Arial,sans-serif)";

  var GROUP_TITLES = [
    { id: 'first_name', title: 'Contact Information', first: true },
    { id: 'custom#pickup_address', title: 'Pickup Location' },
    { id: 'custom#safe_model', title: 'Safe Information' },
    { id: 'custom#destination_address', title: 'Destination Location' },
    { id: '[data-testid="date-birthday"]', title: 'Additional Information', isSelector: true }
  ];

  var CSS = [
    'section[data-sizing="form-wrapper"][role="dialog"]{max-width:1000px !important;width:100% !important;}',

    'form[data-testid="form"]{display:grid !important;grid-template-columns:repeat(12,1fr) !important;gap:16px 18px !important;text-align:left !important;}',
    'form[data-testid="form"] > *{min-width:0;}',
    'form[data-testid="form"] > div:first-child{grid-column:1 / -1 !important;}',

    '.sm-qf-title{grid-column:1 / -1 !important;margin:24px 0 2px !important;font:700 .74rem/1 ' + SANS + ' !important;letter-spacing:.085em !important;text-transform:uppercase !important;color:var(--teal, #3A5F66) !important;}',
    '.sm-qf-title--first{margin-top:0 !important;}',
    '.sm-qf-sublabel{grid-column:1 / -1 !important;margin:0 0 -2px !important;font:600 .82rem/1.3 ' + SANS + ' !important;color:var(--navy, #2A475E) !important;}',

    '._formFieldContainer_1mxsl_5:has(textarea)::before{background-color:transparent !important;}',

    '.sm-qf-group{grid-column:1 / -1 !important;border-bottom:1px solid var(--line, #e2e2df) !important;}',
    '.sm-qf-group--first{border-top:1px solid var(--line, #e2e2df) !important;margin-top:8px !important;}',
    '.sm-qf-accordion-toggle{all:unset;box-sizing:border-box !important;display:flex !important;align-items:center !important;justify-content:space-between !important;width:100% !important;padding:18px 2px 16px !important;cursor:pointer !important;font:700 .74rem/1 ' + SANS + ' !important;letter-spacing:.085em !important;text-transform:uppercase !important;color:var(--teal, #3A5F66) !important;}',
    '.sm-qf-accordion-icon{flex:none !important;width:9px !important;height:9px !important;margin-left:12px !important;border-right:2px solid var(--navy, #2A475E) !important;border-bottom:2px solid var(--navy, #2A475E) !important;transform:rotate(45deg) !important;transition:transform .2s ease !important;}',
    '.sm-qf-group.is-open .sm-qf-accordion-icon{transform:rotate(-135deg) !important;}',
    '.sm-qf-accordion-panel{display:none !important;}',
    '.sm-qf-group.is-open .sm-qf-accordion-panel{display:grid !important;grid-template-columns:repeat(12,1fr) !important;gap:16px 18px !important;padding:0 2px 20px !important;}',

    'form[data-testid="form"] > output{grid-column:1 / -1 !important;display:block !important;margin:0 !important;padding:10px 14px !important;font:500 .85rem/1.4 ' + SANS + ' !important;color:var(--form-error-color, #E02229) !important;background:#E0222912 !important;border:1px solid #E0222933 !important;border-top:0 !important;}',
    'form[data-testid="form"] > output:first-of-type{margin-top:16px !important;border-top:1px solid #E0222933 !important;border-radius:8px 8px 0 0 !important;}',
    'form[data-testid="form"] > output:last-of-type{border-radius:0 0 8px 8px !important;}',
    'form[data-testid="form"] > output:only-of-type{border-radius:8px !important;}',
    '._formFieldContainer_1mxsl_5 > output._formError_1mxsl_21{grid-column:unset !important;display:block !important;margin:6px 0 0 !important;padding:0 !important;background:transparent !important;border:0 !important;}',

    'form[data-testid="form"] div:has(> input:not([type="radio"]) + label),',
    'form[data-testid="form"] div:has(> textarea + label){display:flex !important;flex-direction:column-reverse !important;align-items:stretch !important;}',

    'form[data-testid="form"] label[id^="label-"]{position:static !important;top:auto !important;transform:none !important;display:block !important;margin:0 0 6px !important;font:600 .82rem/1.3 ' + SANS + ' !important;color:var(--navy, #2A475E) !important;}',

    'form[data-testid="form"] input:not([type="radio"]),',
    'form[data-testid="form"] textarea{width:100% !important;box-sizing:border-box !important;font:400 1rem/1.4 ' + SANS + ' !important;color:var(--ink, #1f2733) !important;background:var(--paper, #ffffff) !important;border:1px solid var(--line, #e2e2df) !important;border-radius:9px !important;padding:11px 13px !important;}',
    'form[data-testid="form"] input:not([type="radio"]):focus,',
    'form[data-testid="form"] textarea:focus{border-color:var(--teal, #3A5F66) !important;outline:none !important;}',
    'form[data-testid="form"] textarea{min-height:104px !important;resize:vertical !important;}',

    'form[data-testid="form"] div:has(> input[data-testid="field-first_name"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-last_name"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-email"]),',
    'form[data-testid="form"] div:has(> .phone-country-selector){grid-column:span 6 !important;}',

    'form[data-testid="form"] div:has(> .phone-country-selector){display:flex !important;align-items:flex-end !important;gap:8px !important;}',
    'form[data-testid="form"] div:has(> .phone-country-selector) > .phone-country-selector{flex:none !important;}',
    'form[data-testid="form"] div:has(> .phone-country-selector) > div:has(input[data-testid="field-phone_number"]){flex:1 1 auto !important;min-width:0 !important;}',
    '.phone-country-selector button[aria-label^="Country code"]{display:flex !important;align-items:center !important;height:44px !important;padding:0 12px !important;border:1px solid var(--line, #e2e2df) !important;border-radius:9px !important;background:var(--paper, #fff) !important;box-sizing:border-box !important;}',

    'form[data-testid="form"] div:has(> input[data-testid="field-custom#pickup_address"]),',
    'form[data-testid="form"] div:has(> textarea[name="custom#pickup_location"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#safe_model"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#destination_address"]),',
    'form[data-testid="form"] div:has(> textarea[name="custom#destination_location"]),',
    'form[data-testid="form"] div:has(> textarea[name="custom#anything_else_we_need_to_know"]){grid-column:1 / -1 !important;}',

    'form[data-testid="form"] div:has(> input[data-testid="field-custom#city"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#destination_city"]){grid-column:span 6 !important;}',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#pickup_state"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#pickup_zip_code"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#destination_state"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#destination_zip_code"]){grid-column:span 3 !important;}',

    'form[data-testid="form"] div:has(> input[data-testid="field-custom#height"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#width"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#depth"]){grid-column:span 4 !important;}',

    'form[data-testid="form"] fieldset[name="custom#_do_you_want_the_safe_bolted_down"]{grid-column:1 / -1 !important;border:0 !important;margin:0 !important;padding:0 !important;}',
    'form[data-testid="form"] fieldset[name="custom#_do_you_want_the_safe_bolted_down"] > legend{padding:0 !important;width:auto !important;float:none !important;margin:0 0 10px !important;font:600 .82rem/1.3 ' + SANS + ' !important;color:var(--navy, #2A475E) !important;}',
    'form[data-testid="form"] fieldset[name="custom#_do_you_want_the_safe_bolted_down"] > div{display:flex !important;flex-direction:column !important;gap:10px !important;}',
    'form[data-testid="form"] fieldset[name="custom#_do_you_want_the_safe_bolted_down"] label{display:flex !important;align-items:center !important;gap:12px !important;padding:14px 16px !important;border:1px solid var(--line, #e2e2df) !important;border-radius:10px !important;cursor:pointer !important;font:400 1rem/1.3 ' + SANS + ' !important;color:var(--ink, #1f2733) !important;}',
    'form[data-testid="form"] fieldset[name="custom#_do_you_want_the_safe_bolted_down"] label:has(input:checked){border-color:var(--navy, #2A475E) !important;background:var(--warmgrey, #f6f7f5) !important;}',
    'form[data-testid="form"] fieldset[name="custom#_do_you_want_the_safe_bolted_down"] input[type="radio"]{accent-color:var(--navy, #2A475E) !important;width:18px !important;height:18px !important;margin:0 !important;flex:none !important;}',

    'form[data-testid="form"] [data-testid="date-birthday"]{grid-column:1 / -1 !important;}',
    'form[data-testid="form"] [data-testid="date-birthday"] > div:first-child{margin:0 0 10px !important;font:600 .82rem/1.3 ' + SANS + ' !important;color:var(--navy, #2A475E) !important;}',
    'form[data-testid="form"] [data-testid="date-birthday"] > div:last-child{display:grid !important;grid-template-columns:repeat(3,1fr) !important;gap:16px 18px !important;}',

    'form[data-testid="form"] button[data-testid="btn-form-submit"]{grid-column:1 / -1 !important;width:100% !important;margin-top:8px !important;background:var(--navy, #2A475E) !important;color:#fff !important;border:0 !important;border-radius:9px !important;padding:16px 24px !important;font:600 1rem/1 ' + SANS + ' !important;cursor:pointer !important;}',
    'form[data-testid="form"] button[data-testid="btn-form-submit"]:hover{opacity:.85 !important;}',

    '@media (max-width:600px){',
    'form[data-testid="form"]{gap:14px 16px !important;}',
    'form[data-testid="form"] div:has(> input[data-testid="field-first_name"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-last_name"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-email"]),',
    'form[data-testid="form"] div:has(> .phone-country-selector),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#city"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#destination_city"]){grid-column:1 / -1 !important;}',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#pickup_state"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#pickup_zip_code"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#destination_state"]),',
    'form[data-testid="form"] div:has(> input[data-testid="field-custom#destination_zip_code"]){grid-column:span 6 !important;}',
    '}'
  ].join('\n');

  function findShadowRoots(node, found) {
    found = found || [];
    if (!node) return found;
    if (node.shadowRoot) {
      found.push(node.shadowRoot);
      findShadowRoots(node.shadowRoot, found);
    }
    var children = node.children || [];
    for (var i = 0; i < children.length; i++) findShadowRoots(children[i], found);
    return found;
  }

  function insertSublabels(root) {
    var heightInput = root.getElementById('custom#height');
    if (heightInput && heightInput.parentElement && heightInput.parentElement.parentElement) {
      var hc = heightInput.parentElement;
      var span = document.createElement('p');
      span.className = 'sm-qf-sublabel';
      span.textContent = 'Safe Dimensions';
      hc.parentElement.insertBefore(span, hc);
    }
  }

  function findGroupContainer(root, g) {
    var ref = g.isSelector ? root.querySelector(g.id) : root.getElementById(g.id);
    if (!ref) return null;
    return (ref.tagName === 'INPUT' || ref.tagName === 'TEXTAREA') ? ref.parentElement : ref;
  }

  function buildAccordions(root, form) {
    var starts = GROUP_TITLES.map(function (g) { return findGroupContainer(root, g); });
    var submitBtn = form.querySelector('button[data-testid="btn-form-submit"]');
    var doc = root.ownerDocument || document;

    GROUP_TITLES.forEach(function (g, i) {
      var startEl = starts[i];
      if (!startEl || !startEl.parentElement) return;

      var endEl = null;
      for (var j = i + 1; j < starts.length; j++) {
        if (starts[j]) { endEl = starts[j]; break; }
      }

      var group = doc.createElement('div');
      group.className = 'sm-qf-group' + (g.first ? ' sm-qf-group--first is-open' : '');

      var toggle = doc.createElement('button');
      toggle.type = 'button';
      toggle.className = 'sm-qf-accordion-toggle';
      toggle.setAttribute('aria-expanded', g.first ? 'true' : 'false');

      var titleSpan = doc.createElement('span');
      titleSpan.className = 'sm-qf-accordion-title';
      titleSpan.textContent = g.title;

      var iconSpan = doc.createElement('span');
      iconSpan.className = 'sm-qf-accordion-icon';
      iconSpan.setAttribute('aria-hidden', 'true');

      toggle.appendChild(titleSpan);
      toggle.appendChild(iconSpan);

      var panel = doc.createElement('div');
      panel.className = 'sm-qf-accordion-panel';

      toggle.addEventListener('click', function () {
        var isOpen = group.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
      });

      startEl.parentElement.insertBefore(group, startEl);

      var node = startEl;
      while (node && node !== endEl && node !== submitBtn) {
        var next = node.nextElementSibling;
        panel.appendChild(node);
        node = next;
      }

      group.appendChild(toggle);
      group.appendChild(panel);
    });
  }

  function openGroupFor(el) {
    var group = el.closest ? el.closest('.sm-qf-group') : null;
    if (!group || group.classList.contains('is-open')) return;
    group.classList.add('is-open');
    var toggle = group.querySelector('.sm-qf-accordion-toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  function relocateErrors(form) {
    var strayOutputs = Array.prototype.filter.call(form.children, function (c) { return c.tagName === 'OUTPUT'; });
    if (!strayOutputs.length) return;

    var targets = Array.prototype.filter.call(form.querySelectorAll('.sm-qf-accordion-panel ._formFieldContainer_1mxsl_5'), function (c) {
      return !c.querySelector('output') && (c.className.indexOf('_withError_1mxsl_17') !== -1 || !!c.querySelector('[aria-invalid="true"]'));
    });

    var n = Math.min(strayOutputs.length, targets.length);
    for (var i = 0; i < n; i++) {
      targets[i].insertBefore(strayOutputs[i], targets[i].firstChild);
    }
  }

  function watchForErrors(form) {
    var observer = new MutationObserver(function () {
      form.querySelectorAll('[aria-invalid="true"], ._withError_1mxsl_17, ._invalid_17mgw_14, ._withError_rzr7e_21').forEach(openGroupFor);
      relocateErrors(form);
    });
    observer.observe(form, { attributes: true, attributeFilter: ['aria-invalid', 'class'], subtree: true, childList: true });
  }

  function enhanceRoot(root) {
    var form = root.querySelector('form[data-testid="form"]');
    if (!form || form.hasAttribute('data-sm-enhanced')) return;

    if (!root.getElementById('sm-quote-form-style')) {
      var style = document.createElement('style');
      style.id = 'sm-quote-form-style';
      style.textContent = CSS;
      root.appendChild(style);
    }

    insertSublabels(root);
    buildAccordions(root, form);
    watchForErrors(form);
    form.setAttribute('data-sm-enhanced', '1');
  }

  function scan() {
    var cards = document.querySelectorAll('.sm-quoteform');
    cards.forEach(function (card) {
      var roots = findShadowRoots(card);
      roots.forEach(enhanceRoot);
    });
  }

  function init() {
    var ticks = 0;
    var fastTimer = setInterval(function () {
      scan();
      ticks++;
      if (ticks >= 25) {
        clearInterval(fastTimer);
        setInterval(scan, 4000);
      }
    }, 400);
    scan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
